<template>
  <!-- Dashboard Overview with Workshop Portal Theme -->
  <div class="max-w-7xl mx-auto py-8">
    <!-- Page Header -->
    <div class="mb-8">
      <div class="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-8 text-white shadow-xl">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold mb-2">Dashboard Overview</h1>
            <p class="text-orange-100 text-lg">Monitor your business performance and key metrics</p>
          </div>
          <div class="flex space-x-3">
            <button 
              @click="loadDashboardData"
              :disabled="loading"
              class="px-6 py-3 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl hover:bg-opacity-30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-white"
            >
              {{ loading ? 'Loading...' : 'Refresh' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <!-- Total Users Card -->
      <router-link to="/admin/users" class="group bg-white overflow-hidden shadow-lg rounded-xl hover:shadow-xl transition-shadow cursor-pointer">
        <div class="p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Total Users</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.totalUsers || 0 }}</p>
            </div>
          </div>
        </div>
      </router-link>

      <!-- Total Products Card -->
      <router-link to="/admin/products" class="group bg-white overflow-hidden shadow-lg rounded-xl hover:shadow-xl transition-shadow cursor-pointer">
        <div class="p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Total Products</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.totalProducts || 0 }}</p>
            </div>
          </div>
        </div>
      </router-link>

      <!-- Pending Orders Card -->
      <router-link to="/admin/orders" class="group bg-white overflow-hidden shadow-lg rounded-xl hover:shadow-xl transition-shadow cursor-pointer">
        <div class="p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Pending Orders</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.pendingOrders || 0 }}</p>
            </div>
          </div>
        </div>
      </router-link>

      <!-- Workshop Jobs Card -->
      <router-link to="/admin/workshop" class="group bg-white overflow-hidden shadow-lg rounded-xl hover:shadow-xl transition-shadow cursor-pointer">
        <div class="p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">Workshop Jobs</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.workshopJobs || 0 }}</p>
            </div>
          </div>
        </div>
      </router-link>
    </div>

    <!-- Generate Invite Token Section -->
    <div class="bg-white shadow-lg rounded-xl overflow-hidden mb-8">
      <div class="px-6 py-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-900">Generate Invite Token</h3>
        <p class="text-sm text-gray-600 mt-1">Create secure invite tokens for customer registration</p>
      </div>
      <div class="p-6">
        <form @submit.prevent="generateInviteToken" class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
              <input 
                v-model="inviteForm.company_name" 
                type="text" 
                required
                placeholder="Enter company name"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Expiry (hours)</label>
              <input 
                v-model="inviteForm.expiry" 
                type="number" 
                min="1"
                max="168"
                placeholder="24"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input 
                v-model="inviteForm.email" 
                type="email" 
                placeholder="customer@company.com"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Phone</label>
              <input 
                v-model="inviteForm.phone" 
                type="tel" 
                placeholder="+91 9876543210"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Max Login Attempts</label>
              <input 
                v-model="inviteForm.max_attempts" 
                type="number" 
                min="1"
                max="10"
                placeholder="5"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
              <p class="text-xs text-gray-500 mt-1">Default: 5 attempts</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Session Timeout (minutes)</label>
              <input 
                v-model="inviteForm.session_timeout_minutes" 
                type="number" 
                min="5"
                max="1440"
                placeholder="30"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
              <p class="text-xs text-gray-500 mt-1">Default: 30 minutes</p>
            </div>
          </div>
          
          <div class="flex justify-end">
            <button 
              type="submit"
              :disabled="generatingToken"
              class="px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="generatingToken">Generating...</span>
              <span v-else>Generate Token</span>
            </button>
          </div>
        </form>
        
        <!-- Generated Token Display -->
        <div v-if="generatedToken" class="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h4 class="text-lg font-semibold text-green-800 mb-2">Token Generated Successfully!</h4>
          <div class="bg-white p-3 rounded border border-green-300">
            <p class="text-sm text-gray-600 mb-1">Token:</p>
            <p class="font-mono text-lg font-bold text-green-700 break-all">{{ generatedToken }}</p>
          </div>
          <p class="text-sm text-green-700 mt-2">
            Copy this token and share it with the customer (it is not sent automatically). It expires in {{ generatedExpiryHours }} hours.
          </p>
        </div>
        
        <!-- Error Display -->
        <div v-if="tokenError" class="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-red-700">{{ tokenError }}</p>
        </div>
      </div>
    </div>

    <!-- Recent Activity -->
    <div class="bg-white shadow-lg rounded-xl overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-900">Recent Activity</h3>
      </div>
      <div class="p-6">
        <div class="space-y-6">
          <div v-if="recentActivity.length === 0" class="text-center py-8">
            <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p class="text-gray-500 text-lg">No recent activity</p>
          </div>
          <div v-else v-for="activity in recentActivity" :key="activity.id" 
               class="flex items-center space-x-4 p-4 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
               @click="handleActivityClick(activity)">
            <div class="flex-shrink-0">
              <div class="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                <svg class="h-5 w-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-lg font-semibold text-gray-900">{{ activity.message }}</p>
              <p class="text-sm text-gray-500">{{ activity.time }}</p>
            </div>
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { adminAPI } from '@/services/api'

export default {
  name: 'AdminDashboard',
  setup() {
    const router = useRouter()
    const stats = ref({
      totalUsers: 0,
      totalProducts: 0,
      pendingOrders: 0,
      workshopJobs: 0
    })
    
    const recentActivity = ref([])
    const loading = ref(true)
    
    // Invite token generation state
    const inviteForm = ref({
      company_name: '',
      email: '',
      phone: '',
      expiry: 24,
      max_attempts: 5,
      session_timeout_minutes: 30
    })
    const generatingToken = ref(false)
    const generatedToken = ref('')
    const generatedExpiryHours = ref(24)
    const tokenError = ref('')
    
    const generateInviteToken = async () => {
      try {
        generatingToken.value = true
        tokenError.value = ''
        generatedToken.value = ''
        
        const response = await adminAPI.generateInviteToken(inviteForm.value)
        
        generatedToken.value = response.data.invite.token
        generatedExpiryHours.value = inviteForm.value.expiry || 24
        
        // Reset form
        inviteForm.value = {
          company_name: '',
          email: '',
          phone: '',
          expiry: 24,
          max_attempts: 5,
          session_timeout_minutes: 30
        }
        
      } catch (error) {
        console.error('Failed to generate invite token:', error)
        tokenError.value = error.response?.data?.message || 'Failed to generate invite token'
      } finally {
        generatingToken.value = false
      }
    }
    
    const loadDashboardData = async () => {
      try {
        loading.value = true
        
        // Fetch dashboard statistics from backend
        const dashboardResponse = await adminAPI.getDashboardStats()
        const dashboardData = dashboardResponse.data.stats
        
        // Update stats with real data
        stats.value = {
          totalUsers: dashboardData.users.total || 0,
          totalProducts: dashboardData.products.total || 0,
          pendingOrders: dashboardData.orders.by_status?.pending || 0,
          workshopJobs: dashboardData.orders.workshop_jobs || 0
        }
        
        // Generate recent activity from real data
        const activities = []
        
        // Add recent orders
        if (dashboardData.orders.by_status?.pending > 0) {
          activities.push({
            id: 'pending-orders',
            message: `${dashboardData.orders.by_status.pending} pending orders need attention`,
            time: 'Recently'
          })
        }
        
        // Add workshop jobs
        if (dashboardData.orders.workshop_jobs > 0) {
          activities.push({
            id: 'workshop-jobs',
            message: `${dashboardData.orders.workshop_jobs} workshop jobs in progress`,
            time: 'Recently'
          })
        }
        
        // Add recent products
        if (dashboardData.products.total > 0) {
          activities.push({
            id: 'total-products',
            message: `${dashboardData.products.total} products in catalogue`,
            time: 'Recently'
          })
        }
        
        // Add revenue info
        if (dashboardData.orders.revenue > 0) {
          activities.push({
            id: 'total-revenue',
            message: `Total revenue: ₹${dashboardData.orders.revenue.toLocaleString()}`,
            time: 'Recently'
          })
        }
        
        recentActivity.value = activities
        
      } catch (error) {
        console.error('Failed to load dashboard data:', error)
        // Fallback to empty data
        stats.value = {
          totalUsers: 0,
          totalProducts: 0,
          pendingOrders: 0,
          workshopJobs: 0
        }
        recentActivity.value = []
      } finally {
        loading.value = false
      }
    }
    
    onMounted(() => {
      loadDashboardData()
    })

    const handleActivityClick = (activity) => {
      if (activity.message.includes('pending orders')) {
        router.push('/admin/orders')
      } else if (activity.message.includes('workshop jobs')) {
        router.push('/admin/workshop')
      } else if (activity.message.includes('products')) {
        router.push('/admin/products')
      } else if (activity.message.includes('revenue')) {
        router.push('/admin/orders') // Assuming revenue is related to orders
      }
    }
    
    return {
      stats,
      recentActivity,
      loading,
      handleActivityClick,
      inviteForm,
      generatingToken,
      generatedToken,
      generatedExpiryHours,
      tokenError,
      generateInviteToken
    }
  }
}
</script> 